"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,text-decoration-line,box-shadow,opacity] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 ring-1 ring-white/10 dark:ring-white/5",
        accent:
          "bg-accent text-accent-foreground shadow-xs hover:bg-accent/90 ring-1 ring-white/10 dark:ring-white/5",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 ring-1 ring-white/10 dark:ring-white/5",
        outline:
          "border border-border bg-background/50 shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-background/30 dark:hover:bg-accent/90 dark:hover:text-accent-foreground ring-1 ring-white/10 dark:ring-white/5",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 ring-1 ring-white/10 dark:ring-white/5",
        ghost:
          "text-foreground hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/90 dark:hover:text-accent-foreground",
        destructiveGhost:
          "text-destructive hover:bg-destructive/10 hover:text-destructive dark:hover:bg-destructive/15 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md gap-1.5 px-3",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface UIButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

/**
 * Button Component based on official shadcn button-01 block
 * Supports all standard variants: default, destructive, outline, secondary, ghost, link
 */
export const UIButton = React.forwardRef<HTMLButtonElement, UIButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

UIButton.displayName = "UIButton"

// Export buttonVariants for reuse in other components
export { buttonVariants }